import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { myTasksAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
  ChevronDown,
  ChevronRight,
  ClipboardList,
  AlertTriangle,
  Clock,
  CheckCircle2,
  CalendarDays,
  FileText,
  MapPin,
  Tent,
  MonitorPlay,
  Layers,
  Rocket,
} from 'lucide-react';

const GROUP_META = {
  private_deliveries: { icon: FileText, accent: 'text-blue-600', dot: 'bg-blue-500' },
  partner_conferences: { icon: MapPin, accent: 'text-violet-600', dot: 'bg-violet-500' },
  regional_in_person: { icon: Tent, accent: 'text-emerald-600', dot: 'bg-emerald-500' },
  pprw: { icon: MonitorPlay, accent: 'text-amber-600', dot: 'bg-amber-500' },
  content_dev: { icon: Layers, accent: 'text-rose-600', dot: 'bg-rose-500' },
  platform: { icon: Rocket, accent: 'text-cyan-600', dot: 'bg-cyan-500' },
};

const fmtDate = (iso) => {
  if (!iso) return 'No date';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const SummaryTile = ({ icon: Icon, label, value, tone, testid }) => (
  <Card className={`p-4 flex items-center gap-3 ${tone}`} data-testid={testid}>
    <div className="w-10 h-10 rounded-lg bg-white/70 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="text-2xl font-bold leading-none">{value}</p>
      <p className="text-xs mt-1 opacity-80">{label}</p>
    </div>
  </Card>
);

const TaskRow = ({ item, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(item.link)}
    data-testid={`my-task-row-${item.id}`}
    className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-muted/60 transition-colors border-b last:border-b-0 group"
  >
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-medium text-foreground truncate group-hover:text-primary">
        {item.title}
      </span>
      <span className="flex items-center gap-2 mt-1">
        <Badge variant="outline" className="text-[10px] font-medium">{item.status}</Badge>
        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <CalendarDays className="w-3 h-3" />
          {fmtDate(item.date)}
        </span>
      </span>
    </span>
    {item.overdue && (
      <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]" data-testid="flag-overdue">
        Overdue
      </Badge>
    )}
    {!item.overdue && item.due_soon && (
      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]" data-testid="flag-due-soon">
        Due soon
      </Badge>
    )}
    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
  </button>
);

const GroupSection = ({ group, onOpen }) => {
  const [open, setOpen] = useState(true);
  const meta = GROUP_META[group.key] || { icon: ClipboardList, accent: 'text-gray-600', dot: 'bg-gray-400' };
  const Icon = meta.icon;
  const overdue = group.items.filter((i) => i.overdue).length;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="overflow-hidden">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            data-testid={`my-tasks-group-${group.key}`}
            className="w-full flex items-center gap-3 px-4 py-3 bg-muted/40 hover:bg-muted/70 transition-colors"
          >
            {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            <Icon className={`w-4 h-4 ${meta.accent}`} />
            <span className="text-sm font-semibold flex-1 text-left">{group.label}</span>
            {overdue > 0 && (
              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]">{overdue} overdue</Badge>
            )}
            <span className={`min-w-[22px] h-[22px] px-1.5 rounded-full ${meta.dot} text-white text-[11px] font-semibold flex items-center justify-center`}>
              {group.count}
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {group.items.length === 0 ? (
            <p className="px-4 py-4 text-sm text-muted-foreground">Nothing tagged to you here.</p>
          ) : (
            <div>
              {group.items.map((item) => (
                <TaskRow key={item.id} item={item} onOpen={onOpen} />
              ))}
            </div>
          )}
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export const MyOpenTasksPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await myTasksAPI.get();
        if (active) setData(res);
      } catch (e) {
        if (active) setData({ groups: [], total_open: 0, overdue_count: 0, due_soon_count: 0 });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user?.id]);

  if (loading) {
    return (
      <div className="p-6 space-y-4" data-testid="my-tasks-loading">
        <Skeleton className="h-8 w-56" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    );
  }

  const groupsWithItems = (data.groups || []).filter((g) => g.count > 0);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="my-open-tasks-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-primary" />
          My Open Tasks
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Everything you're tagged on across all delivery, content and platform work — click any item to open it.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <SummaryTile icon={ClipboardList} label="Open items" value={data.total_open} tone="bg-slate-50 text-slate-700" testid="my-tasks-total" />
        <SummaryTile icon={AlertTriangle} label="Overdue" value={data.overdue_count} tone="bg-rose-50 text-rose-700" testid="my-tasks-overdue" />
        <SummaryTile icon={Clock} label="Due in 7 days" value={data.due_soon_count} tone="bg-amber-50 text-amber-700" testid="my-tasks-due-soon" />
      </div>

      {data.total_open === 0 ? (
        <Card className="p-10 text-center" data-testid="my-tasks-empty">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <p className="text-sm font-medium">You're all caught up!</p>
          <p className="text-xs text-muted-foreground mt-1">
            No open items are currently tagged to {data.user_name || 'you'}.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {groupsWithItems.map((group) => (
            <GroupSection key={group.key} group={group} onOpen={(link) => navigate(link)} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MyOpenTasksPage;
